import { db } from "../../firebase";
import {
  collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, serverTimestamp
} from "firebase/firestore";
import { getSpecialtyCollection, getActiveSpecialty } from "util/specialtyCollections";
import { shouldShowCodes } from "util/specialtyDisplayHelper";
import {
  SUBJECTS_FETCH_REQUEST, SUBJECTS_FETCH_SUCCESS, SUBJECTS_FETCH_FAILURE,
  SUBJECT_CREATE_REQUEST, SUBJECT_CREATE_SUCCESS, SUBJECT_CREATE_FAILURE,
  SUBJECT_UPDATE_REQUEST, SUBJECT_UPDATE_SUCCESS, SUBJECT_UPDATE_FAILURE,
  SUBJECT_DELETE_REQUEST, SUBJECT_DELETE_SUCCESS, SUBJECT_DELETE_FAILURE,
  UNITS_FETCH_REQUEST, UNITS_FETCH_SUCCESS, UNITS_FETCH_FAILURE,
  UNIT_CREATE_REQUEST, UNIT_CREATE_SUCCESS, UNIT_CREATE_FAILURE,
  UNIT_UPDATE_REQUEST, UNIT_UPDATE_SUCCESS, UNIT_UPDATE_FAILURE,
  UNIT_DELETE_REQUEST, UNIT_DELETE_SUCCESS, UNIT_DELETE_FAILURE,
  TOPICS_FETCH_REQUEST, TOPICS_FETCH_SUCCESS, TOPICS_FETCH_FAILURE,
  TOPIC_CREATE_REQUEST, TOPIC_CREATE_SUCCESS, TOPIC_CREATE_FAILURE,
  TOPIC_UPDATE_REQUEST, TOPIC_UPDATE_SUCCESS, TOPIC_UPDATE_FAILURE,
  TOPIC_DELETE_REQUEST, TOPIC_DELETE_SUCCESS, TOPIC_DELETE_FAILURE,
  SUBJECTS_UNITS_RESET
} from "./SubjectsUnitsTypes";

// Reset
export const resetSubjectsUnits = () => ({ type: SUBJECTS_UNITS_RESET });

const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

// ========== SUBJECTS ACTIONS ==========

// Fetch all subjects
export const fetchSubjects = () => {
  return async (dispatch, getState) => {
    try {
      // Return cached data if available and fresh
      const subjectsState = getState().questionBankSubjects;
      if (subjectsState.subjects.length > 0 && subjectsState.lastFetchedAt && (Date.now() - subjectsState.lastFetchedAt) < CACHE_TTL_MS) {
        return;
      }

      dispatch({ type: SUBJECTS_FETCH_REQUEST });

      const subjectsRef = collection(db, getSpecialtyCollection("questionBankSubjects", getState));
      const snapshot = await getDocs(subjectsRef);

      const subjects = snapshot.docs.map(doc => ({
        subjectId: doc.id,
        ...doc.data()
      }));

      // Sort client-side: by code for PMR, by name for Pain/Sports
      const showCodes = shouldShowCodes(getActiveSpecialty(getState));
      subjects.sort((a, b) => showCodes
        ? (a.code || '').localeCompare(b.code || '')
        : (a.name || '').localeCompare(b.name || ''));

      dispatch({ type: SUBJECTS_FETCH_SUCCESS, payload: subjects });
    } catch (error) {
      dispatch({ type: SUBJECTS_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create subject
export const createSubject = (subjectData, userEmail) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: SUBJECT_CREATE_REQUEST });

      const subjectsRef = collection(db, getSpecialtyCollection("questionBankSubjects", getState));
      const newSubject = {
        code: subjectData.code || "",
        name: subjectData.name,
        description: subjectData.description || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userEmail,
        isActive: true
      };

      const docRef = await addDoc(subjectsRef, newSubject);

      const createdSubject = {
        subjectId: docRef.id,
        ...newSubject,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: SUBJECT_CREATE_SUCCESS, payload: createdSubject });
      return { success: true, subjectId: docRef.id };
    } catch (error) {
      dispatch({ type: SUBJECT_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update subject
export const updateSubject = (subjectId, subjectData) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: SUBJECT_UPDATE_REQUEST });

      const subjectRef = doc(db, getSpecialtyCollection("questionBankSubjects", getState), subjectId);
      const updateData = {
        ...subjectData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(subjectRef, updateData);

      const updatedSubject = {
        subjectId,
        ...subjectData,
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: SUBJECT_UPDATE_SUCCESS, payload: updatedSubject });
      return { success: true };
    } catch (error) {
      dispatch({ type: SUBJECT_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Delete subject (check if it has units or questions first)
export const deleteSubject = (subjectId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: SUBJECT_DELETE_REQUEST });

      // Check if subject has units
      const unitsRef = collection(db, getSpecialtyCollection("questionBankUnits", getState));
      const unitsQuery = query(unitsRef, where("subjectId", "==", subjectId));
      const unitsSnapshot = await getDocs(unitsQuery);

      if (!unitsSnapshot.empty) {
        throw new Error("Cannot delete subject with existing units. Please delete all units first.");
      }

      // Check if subject has questions
      const questionsRef = collection(db, getSpecialtyCollection("questionBankQuestions", getState));
      const questionsQuery = query(questionsRef, where("subjectId", "==", subjectId));
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty) {
        throw new Error("Cannot delete subject with existing questions. Please delete all questions first.");
      }

      // Check if subject has passages
      const passagesRef = collection(db, getSpecialtyCollection("questionBankPassages", getState));
      const passagesQuery = query(passagesRef, where("subjectId", "==", subjectId));
      const passagesSnapshot = await getDocs(passagesQuery);

      if (!passagesSnapshot.empty) {
        throw new Error("Cannot delete subject with existing passages. Please delete all passages first.");
      }

      // Delete subject
      const subjectRef = doc(db, getSpecialtyCollection("questionBankSubjects", getState), subjectId);
      await deleteDoc(subjectRef);

      dispatch({ type: SUBJECT_DELETE_SUCCESS, payload: subjectId });
      return { success: true };
    } catch (error) {
      dispatch({ type: SUBJECT_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// ========== UNITS ACTIONS ==========

// Fetch all units (optionally filter by subjectId)
export const fetchUnits = (subjectId = null) => {
  return async (dispatch, getState) => {
    try {
      // Return cached data if fetching all (no filter) and cache is fresh
      if (!subjectId) {
        const unitsState = getState().questionBankUnits;
        if (unitsState.units.length > 0 && unitsState.lastFetchedAt && (Date.now() - unitsState.lastFetchedAt) < CACHE_TTL_MS) {
          return;
        }
      }

      dispatch({ type: UNITS_FETCH_REQUEST });

      const unitsRef = collection(db, getSpecialtyCollection("questionBankUnits", getState));
      let q;

      if (subjectId) {
        q = query(unitsRef, where("subjectId", "==", subjectId));
      } else {
        q = query(unitsRef);
      }

      const snapshot = await getDocs(q);

      const units = snapshot.docs.map(doc => ({
        unitId: doc.id,
        ...doc.data()
      }));

      // Sort client-side: by code for PMR, by name for Pain/Sports
      const showCodes = shouldShowCodes(getActiveSpecialty(getState));
      units.sort((a, b) => showCodes
        ? (a.code || '').localeCompare(b.code || '')
        : (a.name || '').localeCompare(b.name || ''));

      dispatch({ type: UNITS_FETCH_SUCCESS, payload: units });
    } catch (error) {
      dispatch({ type: UNITS_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create unit
export const createUnit = (unitData, userEmail) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: UNIT_CREATE_REQUEST });

      const unitsRef = collection(db, getSpecialtyCollection("questionBankUnits", getState));
      const newUnit = {
        subjectId: unitData.subjectId,
        code: unitData.code || "",
        name: unitData.name,
        description: unitData.description || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userEmail,
        isActive: true
      };

      const docRef = await addDoc(unitsRef, newUnit);

      const createdUnit = {
        unitId: docRef.id,
        ...newUnit,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: UNIT_CREATE_SUCCESS, payload: createdUnit });
      return { success: true, unitId: docRef.id };
    } catch (error) {
      dispatch({ type: UNIT_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update unit
export const updateUnit = (unitId, unitData) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: UNIT_UPDATE_REQUEST });

      const unitRef = doc(db, getSpecialtyCollection("questionBankUnits", getState), unitId);
      const updateData = {
        ...unitData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(unitRef, updateData);

      const updatedUnit = {
        unitId,
        ...unitData,
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: UNIT_UPDATE_SUCCESS, payload: updatedUnit });
      return { success: true };
    } catch (error) {
      dispatch({ type: UNIT_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Delete unit (check if it has questions first)
export const deleteUnit = (unitId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: UNIT_DELETE_REQUEST });

      // Check if unit has questions
      const questionsRef = collection(db, getSpecialtyCollection("questionBankQuestions", getState));
      const questionsQuery = query(questionsRef, where("unitId", "==", unitId));
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty) {
        throw new Error("Cannot delete unit with existing questions. Please delete all questions first.");
      }

      // Check if unit has passages
      const passagesRef = collection(db, getSpecialtyCollection("questionBankPassages", getState));
      const passagesQuery = query(passagesRef, where("unitId", "==", unitId));
      const passagesSnapshot = await getDocs(passagesQuery);

      if (!passagesSnapshot.empty) {
        throw new Error("Cannot delete unit with existing passages. Please delete all passages first.");
      }

      // Delete unit
      const unitRef = doc(db, getSpecialtyCollection("questionBankUnits", getState), unitId);
      await deleteDoc(unitRef);

      dispatch({ type: UNIT_DELETE_SUCCESS, payload: unitId });
      return { success: true };
    } catch (error) {
      dispatch({ type: UNIT_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// ========== TOPICS ACTIONS ==========

// Fetch all topics (optionally filter by unitId)
export const fetchTopics = (unitId = null) => {
  return async (dispatch, getState) => {
    try {
      // Return cached data if fetching all (no filter) and cache is fresh
      if (!unitId) {
        const topicsState = getState().questionBankTopics;
        if (topicsState.topics.length > 0 && topicsState.lastFetchedAt && (Date.now() - topicsState.lastFetchedAt) < CACHE_TTL_MS) {
          return;
        }
      }

      dispatch({ type: TOPICS_FETCH_REQUEST });

      const topicsRef = collection(db, getSpecialtyCollection("questionBankTopics", getState));
      let q;

      if (unitId) {
        q = query(topicsRef, where("unitId", "==", unitId));
      } else {
        q = query(topicsRef);
      }

      const snapshot = await getDocs(q);

      const topics = snapshot.docs.map(doc => ({
        topicId: doc.id,
        ...doc.data()
      }));

      // Sort client-side: by code for PMR, by name for Pain/Sports
      const showCodes = shouldShowCodes(getActiveSpecialty(getState));
      topics.sort((a, b) => showCodes
        ? (a.code || '').localeCompare(b.code || '')
        : (a.name || '').localeCompare(b.name || ''));

      dispatch({ type: TOPICS_FETCH_SUCCESS, payload: topics });
    } catch (error) {
      dispatch({ type: TOPICS_FETCH_FAILURE, payload: error.message });
    }
  };
};

// Create topic
export const createTopic = (topicData, userEmail) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: TOPIC_CREATE_REQUEST });

      const topicsRef = collection(db, getSpecialtyCollection("questionBankTopics", getState));
      const newTopic = {
        unitId: topicData.unitId,
        subjectId: topicData.subjectId,
        code: topicData.code || "",
        name: topicData.name,
        description: topicData.description || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userEmail,
        isActive: true
      };

      const docRef = await addDoc(topicsRef, newTopic);

      const createdTopic = {
        topicId: docRef.id,
        ...newTopic,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: TOPIC_CREATE_SUCCESS, payload: createdTopic });
      return { success: true, topicId: docRef.id };
    } catch (error) {
      dispatch({ type: TOPIC_CREATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Update topic
export const updateTopic = (topicId, topicData) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: TOPIC_UPDATE_REQUEST });

      const topicRef = doc(db, getSpecialtyCollection("questionBankTopics", getState), topicId);
      const updateData = {
        ...topicData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(topicRef, updateData);

      const updatedTopic = {
        topicId,
        ...topicData,
        updatedAt: new Date().toISOString()
      };

      dispatch({ type: TOPIC_UPDATE_SUCCESS, payload: updatedTopic });
      return { success: true };
    } catch (error) {
      dispatch({ type: TOPIC_UPDATE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};

// Delete topic (check if it has questions first)
export const deleteTopic = (topicId) => {
  return async (dispatch, getState) => {
    try {
      dispatch({ type: TOPIC_DELETE_REQUEST });

      // Check if topic has questions
      const questionsRef = collection(db, getSpecialtyCollection("questionBankQuestions", getState));
      const questionsQuery = query(questionsRef, where("topicId", "==", topicId));
      const questionsSnapshot = await getDocs(questionsQuery);

      if (!questionsSnapshot.empty) {
        throw new Error("Cannot delete topic with existing questions. Please delete all questions first.");
      }

      // Delete topic
      const topicRef = doc(db, getSpecialtyCollection("questionBankTopics", getState), topicId);
      await deleteDoc(topicRef);

      dispatch({ type: TOPIC_DELETE_SUCCESS, payload: topicId });
      return { success: true };
    } catch (error) {
      dispatch({ type: TOPIC_DELETE_FAILURE, payload: error.message });
      return { success: false, error: error.message };
    }
  };
};
